import React, { useState, useRef, useCallback } from 'react';
import { Play, Pause, Trash2, Scissors, ArrowDown, Wand2, PenLine, Languages, Timer } from 'lucide-react';
import { ReactTransliterate } from 'react-transliterate';
import 'react-transliterate/dist/index.css';
import {
  Utterance,
  SpeakerType,
  SPEAKER_TYPE_LABELS,
  EMOTION_LABELS,
  SENTIMENT_LABELS,
  INTENT_LABELS,
  SPEECH_SPEED_LABELS,
  AUDIO_QUALITY_LABELS,
  SPEECH_MARKER_LABELS,
} from '../../../types/annotation';

// Per-category chip palettes. Each category has its own colour family so a
// reviewer can scan the labels row and tell at a glance which category each
// chip belongs to without reading the tooltip.
const EMOTION_CHIP_COLORS: Record<string, string> = {
  neutral: 'bg-gray-100 text-gray-700 border-gray-200',
  angry: 'bg-red-100 text-red-700 border-red-200',
  sad: 'bg-blue-100 text-blue-700 border-blue-200',
  happy: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  fearful: 'bg-purple-100 text-purple-700 border-purple-200',
  sarcastic: 'bg-pink-100 text-pink-700 border-pink-200',
  frustrated: 'bg-orange-100 text-orange-700 border-orange-200',
  hesitant: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  confident: 'bg-violet-100 text-violet-700 border-violet-200',
  excited: 'bg-amber-100 text-amber-700 border-amber-200',
  disappointed: 'bg-slate-200 text-slate-700 border-slate-300',
};

const SENTIMENT_CHIP_COLORS: Record<string, string> = {
  positive: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  neutral: 'bg-gray-50 text-gray-600 border-gray-200',
  negative: 'bg-red-50 text-red-700 border-red-200',
  mixed: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
};

// Intent is conversational structure — uniform blue tone, all values rendered
// identically. Reviewers tell them apart by the value text alone.
const INTENT_CHIP_CLASS = 'bg-sky-50 text-sky-700 border-sky-200';

// Speech-speed is graded — slow→fast on a teal→amber gradient so anomalies
// (very fast, very slow) stand out.
const SPEECH_SPEED_CHIP_COLORS: Record<string, string> = {
  slow: 'bg-teal-50 text-teal-700 border-teal-200',
  normal: 'bg-gray-50 text-gray-600 border-gray-200',
  fast: 'bg-amber-50 text-amber-700 border-amber-200',
  very_fast: 'bg-red-50 text-red-700 border-red-200',
};

// Audio quality is graded — clean→noisy. "Clear" is green, noise grows redder.
const AUDIO_QUALITY_CHIP_COLORS: Record<string, string> = {
  clear: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  low_background_noise: 'bg-lime-50 text-lime-700 border-lime-200',
  moderate_noise: 'bg-amber-50 text-amber-700 border-amber-200',
  heavy_noise: 'bg-red-50 text-red-700 border-red-200',
  wind_noise: 'bg-orange-50 text-orange-700 border-orange-200',
  microphone_distortion: 'bg-rose-50 text-rose-700 border-rose-200',
};

// Speech markers are paralinguistic events — uniform indigo so they read as
// a group, distinct from the other categories.
const SPEECH_MARKER_CHIP_CLASS = 'bg-indigo-50 text-indigo-700 border-indigo-200';

const CHIP_CLASS_BASE =
  'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap';

interface UtteranceBlockProps {
  utterance: Utterance;
  isSelected: boolean;
  isReviewMode: boolean;
  onSelect: () => void;
  onUpdate: (updates: Partial<Utterance>) => void;
  onDelete: () => void;
  onPlaySegment: (startTime: number, endTime: number) => void;
  onSplit?: (utteranceId: string, splitTime: number) => void;
  currentTime?: number;
  isPlaying?: boolean;
  disabled?: boolean;
}

// Map from utterance.source to auto-utterance flags is not fetched here;
// we use the flags indirectly via the utterance's auto link for future enhancement.
// Source badge renders based on utterance.source alone.

const SPEAKER_COLORS: Partial<Record<SpeakerType, string>> = {
  respondent:    'bg-blue-100 text-blue-700',
  enumerator:    'bg-green-100 text-green-700',
  unknown:       'bg-gray-100 text-gray-600',
  participant_1: 'bg-orange-100 text-orange-700',
  participant_2: 'bg-teal-100 text-teal-700',
  participant_3: 'bg-pink-100 text-pink-700',
};

const DEVANAGARI_FONT: React.CSSProperties = {
  fontFamily: "'Noto Sans Devanagari', 'Noto Sans', sans-serif",
};

const UtteranceBlock: React.FC<UtteranceBlockProps> = ({
  utterance,
  isSelected,
  isReviewMode,
  onSelect,
  onUpdate,
  onDelete,
  onPlaySegment,
  onSplit,
  currentTime = 0,
  isPlaying = false,
  disabled = false,
}) => {
  const isThisSegmentPlaying =
    isPlaying &&
    currentTime >= utterance.startTime - 0.05 &&
    currentTime < utterance.endTime;
  // Local state for textarea buffering
  const [localTranscript, setLocalTranscript] = useState<string>(utterance.transcript ?? '');
  // Timestamp editing state
  const [localStart, setLocalStart] = useState<string>(String(utterance.startTime));
  const [localEnd, setLocalEnd] = useState<string>(String(utterance.endTime));
  const [localNormalized, setLocalNormalized] = useState<string>(
    utterance.normalizedTranscript ?? '',
  );
  // Phonetic (transliteration) mode per field
  const [phoneticLiteral, setPhoneticLiteral] = useState(true);
  const [phoneticNormalized, setPhoneticNormalized] = useState(true);

  // Track whether normalized has ever been explicitly set by user
  const normalizedTouchedRef = useRef<boolean>(
    (utterance.normalizedTranscript ?? '') !== '' &&
      utterance.normalizedTranscript !== utterance.transcript,
  );

  // Keep local state in sync when utterance changes externally (e.g. after save)
  const prevUtteranceIdRef = useRef(utterance.utteranceId);
  if (prevUtteranceIdRef.current !== utterance.utteranceId) {
    prevUtteranceIdRef.current = utterance.utteranceId;
    setLocalTranscript(utterance.transcript ?? '');
    setLocalNormalized(utterance.normalizedTranscript ?? '');
    setLocalStart(String(utterance.startTime));
    setLocalEnd(String(utterance.endTime));
    normalizedTouchedRef.current =
      (utterance.normalizedTranscript ?? '') !== '' &&
      utterance.normalizedTranscript !== utterance.transcript;
    setPhoneticLiteral(true);
    setPhoneticNormalized(true);
  }

  const handleTranscriptChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const val = e.target.value;
      setLocalTranscript(val);
      // One-way sync to normalized if not yet touched
      if (!normalizedTouchedRef.current) {
        setLocalNormalized(val);
      }
    },
    [],
  );

  const handleTranscriptBlur = useCallback(() => {
    const updates: Partial<Utterance> = { transcript: localTranscript };
    if (!normalizedTouchedRef.current) {
      updates.normalizedTranscript = localTranscript;
    }
    onUpdate(updates);
  }, [localTranscript, onUpdate]);

  const handleNormalizedChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const val = e.target.value;
      setLocalNormalized(val);
      normalizedTouchedRef.current = true;
    },
    [],
  );

  const handleNormalizedBlur = useCallback(() => {
    onUpdate({ normalizedTranscript: localNormalized });
  }, [localNormalized, onUpdate]);

  const handleSplitAtMidpoint = useCallback(() => {
    if (!onSplit) return;
    const midpoint = (utterance.startTime + utterance.endTime) / 2;
    onSplit(utterance.utteranceId, midpoint);
  }, [onSplit, utterance]);

  const handleStartBlur = useCallback(() => {
    const val = parseFloat(localStart);
    if (!isNaN(val) && val >= 0 && val < utterance.endTime) {
      onUpdate({ startTime: val });
    } else {
      setLocalStart(String(utterance.startTime));
    }
  }, [localStart, utterance.startTime, utterance.endTime, onUpdate]);

  const handleEndBlur = useCallback(() => {
    const val = parseFloat(localEnd);
    if (!isNaN(val) && val > utterance.startTime) {
      onUpdate({ endTime: val });
    } else {
      setLocalEnd(String(utterance.endTime));
    }
  }, [localEnd, utterance.startTime, utterance.endTime, onUpdate]);

  const isDisabled = disabled || isReviewMode;

  return (
    <div
      onClick={onSelect}
      className={[
        'relative rounded-xl border transition-all cursor-pointer',
        isSelected
          ? 'border-violet-400 bg-violet-50 ring-2 ring-violet-500 shadow-md'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm',
      ].join(' ')}
    >
      {/* Header row */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-inherit">
        {/* Play/Pause button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPlaySegment(utterance.startTime, utterance.endTime);
          }}
          className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition-colors shrink-0"
          title={isThisSegmentPlaying ? 'Pause segment' : 'Play segment'}
        >
          {isThisSegmentPlaying
            ? <Pause className="w-3 h-3" />
            : <Play className="w-3 h-3" />}
          <span>{isThisSegmentPlaying ? 'Pause' : 'Play'}</span>
        </button>

        {/* Timestamps — directly editable when not in review mode */}
        <div className="flex items-center gap-1 shrink-0 font-mono text-xs text-gray-500" onClick={(e) => e.stopPropagation()}>
          <input
            type="number"
            value={localStart}
            onChange={(e) => setLocalStart(e.target.value)}
            onBlur={handleStartBlur}
            disabled={isDisabled}
            step="0.1"
            min="0"
            className="w-16 px-1 py-0.5 text-xs font-mono text-center border border-transparent rounded hover:border-gray-300 focus:border-violet-400 focus:ring-1 focus:ring-violet-400 focus:outline-none disabled:border-transparent bg-transparent"
            title="Start time (seconds)"
          />
          <span>→</span>
          <input
            type="number"
            value={localEnd}
            onChange={(e) => setLocalEnd(e.target.value)}
            onBlur={handleEndBlur}
            disabled={isDisabled}
            step="0.1"
            min="0"
            className="w-16 px-1 py-0.5 text-xs font-mono text-center border border-transparent rounded hover:border-gray-300 focus:border-violet-400 focus:ring-1 focus:ring-violet-400 focus:outline-none disabled:border-transparent bg-transparent"
            title="End time (seconds)"
          />
        </div>

        {/* Speaker selector */}
        <select
          value={utterance.speaker}
          onChange={(e) => {
            e.stopPropagation();
            onUpdate({ speaker: e.target.value as SpeakerType });
          }}
          disabled={isDisabled}
          onClick={(e) => e.stopPropagation()}
          className={[
            'text-xs rounded-lg px-2 py-1 border-0 font-semibold cursor-pointer focus:ring-2 focus:ring-violet-500',
            SPEAKER_COLORS[utterance.speaker] ?? 'bg-gray-100 text-gray-600',
            isDisabled ? 'opacity-60 cursor-not-allowed' : '',
          ].join(' ')}
        >
          {(Object.keys(SPEAKER_TYPE_LABELS) as SpeakerType[]).map((s) => (
            <option key={s} value={s}>
              {SPEAKER_TYPE_LABELS[s]}
            </option>
          ))}
        </select>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Source provenance badge */}
        {utterance.source === 'auto_accepted' && (
          <span
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-600 border border-purple-200"
            title="Generated by Whisper auto-transcription — not yet edited"
          >
            <Wand2 className="w-2.5 h-2.5" />
            Auto
          </span>
        )}
        {utterance.source === 'auto_corrected' && (
          <span
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-600 border border-blue-200"
            title={`Corrected${utterance.lastEditedBy ? ` by ${utterance.lastEditedBy}` : ''}`}
          >
            <PenLine className="w-2.5 h-2.5" />
            Corrected
          </span>
        )}

        {/* Sequence index badge */}
        <span className="text-xs text-gray-400">#{utterance.sequenceIndex + 1}</span>

        {/* Delete */}
        {!isDisabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="p-1 text-gray-400 hover:text-red-500 transition-colors rounded"
            title="Delete segment"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Labels strip — every set annotation surfaced as a chip so a reviewer
          can scan all labels for every segment without opening each one in
          the side panel. Wraps to multiple lines on narrow widths. */}
      {(utterance.emotion ||
        utterance.sentiment ||
        utterance.intent ||
        utterance.speechSpeed ||
        utterance.audioQuality ||
        (utterance.speechMarkers && utterance.speechMarkers.length > 0)) && (
        <div
          className="flex flex-wrap items-center gap-1.5 px-3 pt-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          {utterance.emotion && (
            <span
              className={`${CHIP_CLASS_BASE} ${
                EMOTION_CHIP_COLORS[utterance.emotion] ?? 'bg-gray-100 text-gray-600 border-gray-200'
              }`}
              title={`Emotion: ${EMOTION_LABELS[utterance.emotion]}`}
            >
              {EMOTION_LABELS[utterance.emotion]}
            </span>
          )}
          {utterance.sentiment && (
            <span
              className={`${CHIP_CLASS_BASE} ${
                SENTIMENT_CHIP_COLORS[utterance.sentiment] ?? 'bg-gray-50 text-gray-600 border-gray-200'
              }`}
              title={`Sentiment: ${SENTIMENT_LABELS[utterance.sentiment]}`}
            >
              {SENTIMENT_LABELS[utterance.sentiment]}
            </span>
          )}
          {utterance.intent && (
            <span
              className={`${CHIP_CLASS_BASE} ${INTENT_CHIP_CLASS}`}
              title={`Conversational Intent: ${INTENT_LABELS[utterance.intent]}`}
            >
              {INTENT_LABELS[utterance.intent]}
            </span>
          )}
          {utterance.speechSpeed && (
            <span
              className={`${CHIP_CLASS_BASE} ${
                SPEECH_SPEED_CHIP_COLORS[utterance.speechSpeed] ?? 'bg-gray-50 text-gray-600 border-gray-200'
              }`}
              title={`Speech Speed: ${SPEECH_SPEED_LABELS[utterance.speechSpeed]}`}
            >
              {SPEECH_SPEED_LABELS[utterance.speechSpeed]}
            </span>
          )}
          {utterance.audioQuality && (
            <span
              className={`${CHIP_CLASS_BASE} ${
                AUDIO_QUALITY_CHIP_COLORS[utterance.audioQuality] ?? 'bg-gray-50 text-gray-600 border-gray-200'
              }`}
              title={`Audio Quality: ${AUDIO_QUALITY_LABELS[utterance.audioQuality]}`}
            >
              {AUDIO_QUALITY_LABELS[utterance.audioQuality]}
            </span>
          )}
          {(utterance.speechMarkers ?? []).map((marker) => (
            <span
              key={marker}
              className={`${CHIP_CLASS_BASE} ${SPEECH_MARKER_CHIP_CLASS}`}
              title={`Speech Marker: ${SPEECH_MARKER_LABELS[marker]}`}
            >
              {SPEECH_MARKER_LABELS[marker]}
            </span>
          ))}
        </div>
      )}

      {/* Auto-source flag pills — only shown on auto utterances */}
      {(utterance.source === 'auto_accepted' || utterance.source === 'auto_corrected') && (
        <div className="flex items-center gap-1.5 px-3 pt-1.5" onClick={(e) => e.stopPropagation()}>
          {/* Code-mixed flag — shown when transcript contains >10% Latin chars */}
          {utterance.transcript && /[a-zA-Z]/.test(utterance.transcript) &&
            (utterance.transcript.match(/[a-zA-Z]/g) ?? []).length /
            Math.max(1, (utterance.transcript.match(/\w/g) ?? []).length) > 0.10 && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-600 border border-orange-200"
              title="Code-mixed: contains significant Latin characters alongside Hindi">
              <Languages className="w-2.5 h-2.5" />
              Code-mixed
            </span>
          )}
          {/* Long segment flag — segments over 10s */}
          {(utterance.endTime - utterance.startTime) > 10 && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-600 border border-amber-200"
              title="Long segment: over 10 seconds — consider splitting">
              <Timer className="w-2.5 h-2.5" />
              Long
            </span>
          )}
        </div>
      )}

      {/* Body */}
      <div className="px-3 py-2 space-y-2" onClick={(e) => e.stopPropagation()}>
        {/* Literal transcript */}
        <div>
          <label className="flex items-center justify-between text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
            <span>Literal</span>
            {!isDisabled && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setPhoneticLiteral((v) => !v); }}
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
                  phoneticLiteral
                    ? 'bg-violet-100 text-violet-600 border border-violet-300'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
                title="Toggle phonetic Hindi input — type in English, get Devanagari"
              >
                <Languages className="w-3 h-3" />
                {phoneticLiteral ? 'Phonetic ON' : 'Phonetic'}
              </button>
            )}
          </label>
          {phoneticLiteral && !isDisabled ? (
            <ReactTransliterate
              value={localTranscript}
              onChangeText={(val) => {
                setLocalTranscript(val);
                if (!normalizedTouchedRef.current) setLocalNormalized(val);
              }}
              onBlur={handleTranscriptBlur}
              lang="hi"
              style={DEVANAGARI_FONT}
              className="w-full px-3 py-2 text-sm border border-violet-400 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
              placeholder="Type phonetically…"
              rows={2}
              renderComponent={(props) => <textarea {...props} />}
            />
          ) : (
            <textarea
              value={localTranscript}
              onChange={handleTranscriptChange}
              onBlur={handleTranscriptBlur}
              disabled={isDisabled}
              rows={2}
              placeholder="Transcript..."
              lang="hi"
              style={DEVANAGARI_FONT}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none disabled:opacity-60 disabled:cursor-not-allowed"
            />
          )}
        </div>

        {/* Normalized transcript */}
        <div>
          <label className="flex items-center justify-between text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
            <span>Normalized</span>
            {!isDisabled && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setPhoneticNormalized((v) => !v); }}
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
                  phoneticNormalized
                    ? 'bg-violet-100 text-violet-600 border border-violet-300'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
                title="Toggle phonetic Hindi input — type in English, get Devanagari"
              >
                <Languages className="w-3 h-3" />
                {phoneticNormalized ? 'Phonetic ON' : 'Phonetic'}
              </button>
            )}
          </label>
          {phoneticNormalized && !isDisabled ? (
            <ReactTransliterate
              value={localNormalized}
              onChangeText={(val) => {
                setLocalNormalized(val);
                normalizedTouchedRef.current = true;
              }}
              onBlur={handleNormalizedBlur}
              lang="hi"
              style={DEVANAGARI_FONT}
              className="w-full px-3 py-2 text-sm border border-violet-400 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
              placeholder="Type phonetically…"
              rows={2}
              renderComponent={(props) => <textarea {...props} />}
            />
          ) : (
            <textarea
              value={localNormalized}
              onChange={handleNormalizedChange}
              onBlur={handleNormalizedBlur}
              disabled={isDisabled}
              rows={2}
              placeholder="Normalized transcript..."
              lang="hi"
              style={DEVANAGARI_FONT}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none disabled:opacity-60 disabled:cursor-not-allowed"
            />
          )}
        </div>

        {/* Actions row */}
        {!isDisabled && (
          <div className="flex items-center gap-2 pt-1">
            {onSplit && (
              <button
                type="button"
                onClick={handleSplitAtMidpoint}
                className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                title="Split at midpoint"
              >
                <Scissors className="w-3 h-3" />
                <span>Split</span>
              </button>
            )}
            <button
              type="button"
              className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
              title="Merge with next segment"
            >
              <ArrowDown className="w-3 h-3" />
              <span>Merge ↓</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UtteranceBlock;
